import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Landmark, Wallet, CheckCircle2, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import ProviderTabs from '../components/ProviderTabs';
import BackButton from '../components/BackButton';
import ProviderLoadingScreen from '../components/ProviderLoadingScreen';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Espejo de la lista aceptada por el backend (provider.py _RD_BANKS).
const RD_BANKS = [
  'Banreservas', 'Banco Popular', 'Banco BHD', 'Scotiabank',
  'Banco Santa Cruz', 'Banco Caribe', 'Banco BDI', 'APAP',
  'Banco Promerica', 'Banco Vimenca', 'Banesco', 'Banco Ademi',
  'Banco Lafise', 'ACAP', 'La Nacional', 'Otro',
];

export default function ProviderPayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({
    method: 'bank',
    holder_name: '',
    paypal_email: '',
    bank_name: '',
    account_type: 'ahorro',
    account_number: '',
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetch(`${BACKEND_URL}/api/provider/payout-account`, { credentials: 'include' })
      .then(res => (res.ok ? res.json() : { payout_account: null }))
      .then(data => {
        const acc = data.payout_account;
        setCurrent(acc);
        if (acc) {
          setForm({
            method: acc.method || 'bank',
            holder_name: acc.holder_name || '',
            paypal_email: acc.paypal_email || '',
            bank_name: acc.bank_name || '',
            account_type: acc.account_type || 'ahorro',
            account_number: acc.account_number || '',
          });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [user, authLoading, navigate]);

  const isValid = form.holder_name.trim().length >= 3 && (
    form.method === 'paypal'
      ? /.+@.+\..+/.test(form.paypal_email.trim())
      : form.bank_name && /^\d{4,20}$/.test(form.account_number.replace(/\D/g, ''))
  );

  const handleSave = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      const res = await fetch(`${BACKEND_URL}/api/provider/payout-account`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || t('payout.saveError')); return; }
      setCurrent(data.payout_account);
      setSaved(true);
      refreshUser?.();
      setTimeout(() => setSaved(false), 3500);
    } catch {
      setError(t('payout.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true); setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/provider/payout-account`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setCurrent(null);
        setForm({ method: 'bank', holder_name: '', paypal_email: '', bank_name: '', account_type: 'ahorro', account_number: '' });
        refreshUser?.();
      }
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) return <ProviderLoadingScreen skeleton />;

  const inputClass = 'w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168] text-sm';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <ProviderTabs />
      <div className="container mx-auto px-4 md:px-6 max-w-3xl py-8 pb-24 md:pb-8">
        <BackButton to="/provider/dashboard" />
        <h1 className="text-3xl font-heading font-bold mb-2">{t('payout.title')}</h1>
        <p className="text-gray-500 text-sm mb-6">{t('payout.subtitle')}</p>

        {current && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-6 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <div className="text-sm text-green-800">
              <p className="font-semibold">{t('payout.configuredTitle')}</p>
              <p className="mt-0.5">
                {current.method === 'paypal'
                  ? `PayPal · ${current.paypal_email}`
                  : `${current.bank_name} · ${t(current.account_type === 'corriente' ? 'payout.accountCorriente' : 'payout.accountAhorro')} · ****${(current.account_number || '').slice(-4)}`}
              </p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm">
          {/* Selector de método */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {[
              { id: 'bank', icon: Landmark, label: t('payout.methodBank') },
              { id: 'paypal', icon: Wallet, label: 'PayPal' },
            ].map(m => (
              <button
                key={m.id}
                type="button"
                onClick={() => setForm({ ...form, method: m.id })}
                className={`flex items-center justify-center gap-2 px-4 py-4 rounded-2xl border-2 font-semibold text-sm transition-colors ${
                  form.method === m.id
                    ? 'border-[#134168] bg-[#134168]/5 text-[#134168]'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300'
                }`}
              >
                <m.icon className="w-5 h-5" /> {m.label}
              </button>
            ))}
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('payout.holderName')}</label>
              <input
                type="text"
                value={form.holder_name}
                onChange={e => setForm({ ...form, holder_name: e.target.value })}
                className={inputClass}
                placeholder={t('payout.holderPlaceholder')}
              />
            </div>

            {form.method === 'paypal' ? (
              <div>
                <label className="block text-sm font-medium mb-2">{t('payout.paypalEmail')}</label>
                <input
                  type="email"
                  value={form.paypal_email}
                  onChange={e => setForm({ ...form, paypal_email: e.target.value })}
                  className={inputClass}
                  placeholder="nombre@correo.com"
                />
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('payout.bankName')}</label>
                  <select
                    value={form.bank_name}
                    onChange={e => setForm({ ...form, bank_name: e.target.value })}
                    className={`${inputClass} bg-white`}
                  >
                    <option value="">{t('payout.selectBank')}</option>
                    {RD_BANKS.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('payout.accountType')}</label>
                  <select
                    value={form.account_type}
                    onChange={e => setForm({ ...form, account_type: e.target.value })}
                    className={`${inputClass} bg-white`}
                  >
                    <option value="ahorro">{t('payout.accountAhorro')}</option>
                    <option value="corriente">{t('payout.accountCorriente')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('payout.accountNumber')}</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={form.account_number}
                    onChange={e => setForm({ ...form, account_number: e.target.value.replace(/[^\d]/g, '') })}
                    className={inputClass}
                    placeholder="0000000000"
                    maxLength={20}
                  />
                </div>
              </>
            )}
          </div>

          {error && <p className="text-red-600 text-sm mt-4" role="alert">{error}</p>}
          {saved && <p className="text-green-600 text-sm mt-4" role="status">✓ {t('payout.savedOk')}</p>}

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <button
              onClick={handleSave}
              disabled={!isValid || saving}
              className="flex-1 px-6 py-3 rounded-xl bg-[#134168] text-white font-bold text-sm hover:bg-[#0e3150] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? t('common.loading') : t('payout.save')}
            </button>
            {current && (
              <button
                onClick={handleDelete}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-red-200 text-red-600 font-bold text-sm hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" /> {t('payout.remove')}
              </button>
            )}
          </div>

          <p className="text-xs text-gray-400 mt-5">{t('payout.privacyNote')}</p>
        </div>
      </div>
    </div>
  );
}
